/**
 * Chat Panel Component
 *
 * The main chat interface that can render in multiple styles:
 * - bubble: Small floating panel triggered by a bubble button
 * - immersive: Full-screen takeover (the AI IS the website)
 * - inline: Embedded within a container element
 *
 * Panel visibility is controlled by CSS classes:
 * - Base `.pms-panel` is hidden by default (opacity: 0)
 * - `.pms-panel-open` makes it visible
 * - All styles (bubble, vertical, immersive) start hidden and open via `.pms-panel-open`
 *
 * Architecture:
 * - ChatPanelState: Centralized state management
 * - ChatPanelApi: API communication (streaming, non-streaming, sessions)
 * - ChatPanelRenderer: DOM creation and manipulation
 * - ChatPanel: Orchestration and event handling
 */

import type { WidgetConfig, ChatMessage, MessageBlock } from '../core/config';
import { isAuthenticatedFromStorage } from '../core/config';
import type { ToolRequest, ToolResult } from '../core/api-executor';
import { validateInput, resetRateLimiter } from '../core/input-validator';
import { debug, warn, error as logError } from '../core/logger';
import { CLICK_DEBOUNCE_MS, COPY_FEEDBACK_DURATION_MS } from '../core/constants';
import { showApiKeyModal, hideApiKeyModal } from './api-key-modal';
import { setBlockRendererContext } from './blocks';
import { getDetailStore, resetDetailStore } from '../core/detail-store';
import { detectLanguageFromText, getLanguage, setLanguage, setRtlContainer, SUPPORTED_LANGUAGES } from '../core/i18n';
import type { SupportedLanguage } from '../core/i18n';
import { navigateToUrl, installLinkInterceptor } from '../core/navigation';
import { ChatPanelState, generateMessageId } from './panel/state';
import { ChatPanelApi } from './panel/api';
import { ChatPanelRenderer, type PanelStyle, type PanelElements } from './panel/renderer';
import type { HostPagePusher } from '../core/host-page-pusher';

// =============================================================================
// TYPES
// =============================================================================

export interface PanelCallbacks {
  onClose: () => void;
  onMessageSent?: (message: string) => void;
  onResponseReceived?: (response: string, blocks?: MessageBlock[]) => void;
  onError?: (error: Error) => void;
  onActionClick?: (action: string) => void;
  onDemoSwitch?: (demoId: string) => void;
  /** Handle tool execution requests from AI (for API discovery feature) */
  onToolRequest?: (request: ToolRequest) => Promise<ToolResult>;
  /** Handle style switching (bubble ↔ immersive) */
  onStyleSwitch?: (style: string) => void;
}

// =============================================================================
// CHAT PANEL CLASS
// =============================================================================

export class ChatPanel {
  // --- Configuration ---
  private readonly config: WidgetConfig;
  private readonly callbacks: PanelCallbacks;
  private readonly style: PanelStyle;

  // --- Modules ---
  private readonly state: ChatPanelState;
  private readonly api: ChatPanelApi;
  private readonly renderer: ChatPanelRenderer;

  // --- DOM References ---
  private elements: PanelElements | null = null;
  private dropdownElement: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private typingIndicator: HTMLElement | null = null;
  private apiKeyModal: HTMLElement | null = null;

  // --- Timer Management ---
  private activeTimers: Set<ReturnType<typeof setTimeout>> = new Set();

  // --- Lifecycle ---
  private isDestroyed = false;

  // --- IME Composition (CJK input) ---
  private isComposing = false;

  // --- Event Listener Cleanup References ---
  private boundBeforeUnloadHandler: (() => void) | null = null;
  private boundDocumentClickHandler: (() => void) | null = null;
  private boundDocumentKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private boundCloseHandler: (() => void) | null = null;
  private boundSpeedToggleHandler: (() => void) | null = null;
  private boundSendHandler: (() => void) | null = null;
  private boundInputKeypressHandler: ((e: KeyboardEvent) => void) | null = null;
  private boundMessagesClickHandler: ((e: Event) => void) | null = null;
  private boundMenuHandler: ((e: Event) => void) | null = null;

  // --- Top Handle (immersive drag) ---
  private topHandle: HTMLElement | null = null;
  private floatingBubble: HTMLButtonElement | null = null;
  private isResizingTop = false;
  private resizeStartY = 0;
  private resizeStartTop = 0;
  private containerTop = 0;
  private boundTopMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundTopMouseUp: (() => void) | null = null;
  private boundTopTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundTopTouchEnd: (() => void) | null = null;

  // --- Layout Toggle (immersive ↔ vertical) ---
  private activeLayout: 'immersive' | 'vertical' = 'immersive';
  private layoutToggleBtn: HTMLButtonElement | null = null;
  private floatingLayoutToggle: HTMLButtonElement | null = null;

  // --- Vertical Resize (left-edge width drag) ---
  private verticalResizeHandle: HTMLElement | null = null;
  private isResizingLeft = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private boundLeftMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundLeftMouseUp: (() => void) | null = null;
  private boundLeftTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundLeftTouchEnd: (() => void) | null = null;
  private boundTopHandleMouseDown: ((e: MouseEvent) => void) | null = null;
  private boundTopHandleTouchStart: ((e: TouchEvent) => void) | null = null;

  // --- Bubble Drag ---
  private isDragging = false;
  private dragDidMove = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragPanelStartX = 0;
  private dragPanelStartY = 0;
  private boundDragMouseDown: ((e: MouseEvent) => void) | null = null;
  private boundDragMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundDragMouseUp: ((e: MouseEvent) => void) | null = null;
  private boundDragTouchStart: ((e: TouchEvent) => void) | null = null;
  private boundDragTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundDragTouchEnd: (() => void) | null = null;

  // --- Host Page Pusher ---
  private pusher: HostPagePusher | null = null;

  // =============================================================================
  // CONSTRUCTOR
  // =============================================================================

  constructor(config: WidgetConfig, callbacks: PanelCallbacks) {
    this.config = config;
    this.callbacks = callbacks;

    // Determine panel style
    this.style = this.determineStyle(config.style);

    // Initialize modules
    this.state = new ChatPanelState();
    this.state.loadSpeedModePreference();
    this.api = new ChatPanelApi(config);
    this.renderer = new ChatPanelRenderer(config, this.style);

    // Set block renderer context for data-driven blocks
    setBlockRendererContext(
      this.api.getInternalApiClient(),
      config.workspaceId,
      config.currencyCode ?? 'GBP',
      config.currencySymbol ?? '£',
      config.availableFlows ?? [],
      null,
      config.vertical
    );
  }

  /**
   * Normalize style config to valid PanelStyle
   */
  private determineStyle(configStyle?: string): PanelStyle {
    switch (configStyle) {
      case 'immersive':
        return 'immersive';
      case 'vertical':
        return 'vertical';
      case 'inline':
        return 'inline';
      case 'overlay':
        return 'immersive';
      default:
        return 'bubble';
    }
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /** Set shadow root for modal rendering */
  setShadowRoot(shadowRoot: ShadowRoot): void {
    this.shadowRoot = shadowRoot;
    setBlockRendererContext(
      this.api.getInternalApiClient(),
      this.config.workspaceId,
      this.config.currencyCode ?? 'GBP',
      this.config.currencySymbol ?? '£',
      this.config.availableFlows ?? [],
      shadowRoot,
      this.config.vertical
    );
    // Intercept <a target="_blank"> clicks to use same-page navigation in production
    installLinkInterceptor(shadowRoot);
  }

  /** Create and return the panel DOM element */
  create(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = this.renderer.buildPanelClassName();
    this.renderer.applyThemeProperties(panel);
    panel.innerHTML = this.renderer.buildPanelHTML(this.state.isSpeedModeEnabled());

    // Accessibility: dialog role and label
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', this.config.branding?.companyName || 'Chat');

    this.elements = this.renderer.cacheElementReferences(panel);

    // Register panel for automatic RTL direction switching
    setRtlContainer(panel);

    // Accessibility: messages container is a live log region
    if (this.elements.messagesContainer) {
      this.elements.messagesContainer.setAttribute('role', 'log');
      this.elements.messagesContainer.setAttribute('aria-live', 'polite');
      this.elements.messagesContainer.setAttribute('aria-label', 'Conversation');
    }

    // Always create top handle (needed for runtime style switching bubble ↔ immersive)
    // (split-panel creates its own top handle, so skip when split is enabled)
    if (!this.config.splitPanel?.enabled) {
      this.createTopHandle();
      // Hide for bubble style — will be shown when switching to immersive
      if (this.style === 'bubble') {
        this.topHandle!.style.display = 'none';
      }
    }

    this.setupEventListeners();

    // Try to restore conversation from a previous page navigation
    const restored = this.state.restoreConversation(this.config.workspaceId);
    if (restored.length > 0 && this.elements?.messagesContainer) {
      // Re-render all restored messages
      for (const message of restored) {
        this.renderer.renderMessage(this.elements.messagesContainer, message, {
          features: this.config.features,
          onAction: (action) => this.handleAction(action),
        });
      }
      // Auto-open the panel if it was visible before navigation
      if (this.state.isCurrentlyVisible()) {
        panel.classList.add('pms-panel-open');
      }
    } else {
      this.showWelcome();
    }

    // Save conversation on page unload (covers back/forward, link clicks, etc.)
    this.boundBeforeUnloadHandler = () => {
      this.state.saveConversation(this.config.workspaceId);
    };
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);

    return panel;
  }

  /** Show the panel (add open class) */
  show(): void {
    this.elements?.panel.classList.add('pms-panel-open');
    this.state.setVisible(true);
    this.elements?.inputElement?.focus();
  }

  /** Hide the panel (remove open class) */
  hide(): void {
    this.elements?.panel.classList.remove('pms-panel-open');
    this.state.setVisible(false);
  }

  /** Toggle panel visibility */
  toggle(): void {
    if (this.isOpen()) {
      this.hide();
    } else {
      this.show();
    }
  }

  /** Show top handle (for style switching to immersive) */
  showTopHandle(): void {
    if (this.topHandle) {
      this.topHandle.style.display = '';
      // Set up drag resize if switching from bubble → immersive at runtime
      if (this.activeLayout !== 'vertical' && this.elements) {
        this.containerTop = this.calcInitialContainerTop();
        this.elements.panel.style.setProperty('--container-top', `${this.containerTop}px`);
        this.topHandle.style.cursor = 'ns-resize';
        this.activeLayout = 'immersive';
        this.setupTopHandleResize();
      }
    }
  }

  /** Hide top handle (for style switching to bubble) */
  hideTopHandle(): void {
    if (this.topHandle) {
      this.topHandle.style.display = 'none';
      this.teardownTopHandleResize();
      this.teardownVerticalResize();
    }
  }

  /** Set active layout programmatically (for style switching to vertical/immersive) */
  setActiveLayout(layout: 'vertical' | 'immersive'): void {
    if (!this.elements || !this.topHandle) return;
    const panel = this.elements.panel;

    if (layout === 'vertical' && this.activeLayout !== 'vertical') {
      this.teardownTopHandleResize();
      this.activeLayout = 'vertical';
      this.topHandle.style.cursor = 'default';
      panel.style.removeProperty('--container-top');
      this.setupVerticalResize();
    } else if (layout === 'immersive' && this.activeLayout !== 'immersive') {
      this.teardownVerticalResize();
      this.activeLayout = 'immersive';
      this.topHandle.style.cursor = 'ns-resize';
      panel.style.removeProperty('--panel-width');
      this.containerTop = this.calcInitialContainerTop();
      panel.style.setProperty('--container-top', `${this.containerTop}px`);
      this.setupTopHandleResize();
    }
    this.updateLayoutToggleIcon();
    this.updateFloatingLayoutToggle();
    this.reapplyPush();
  }

  /** Set host page pusher for drag-resize sync. */
  setPusher(pusher: HostPagePusher): void {
    this.pusher = pusher;
  }

  /** Check if panel is currently open */
  isOpen(): boolean {
    return this.elements?.panel.classList.contains('pms-panel-open') ?? false;
  }

  /** Set panel title text */
  setTitle(title: string): void {
    const titleEl = this.elements?.panel.querySelector('.pms-panel-title');
    if (titleEl) {
      titleEl.textContent = title;
    }
  }

  /** Clear chat history and show welcome */
  clear(): void {
    this.state.clearMessages();
    if (this.elements?.messagesContainer) {
      this.renderer.clearMessages(this.elements.messagesContainer);
      this.showWelcome();
    }
  }

  /** Start a new session (clears history and resets session ID) */
  newSession(): void {
    this.api.newSession();
    resetRateLimiter();
    this.state.resetForNewSession();
    this.state.clearSavedConversation(this.config.workspaceId);
    if (this.elements?.messagesContainer) {
      this.renderer.clearMessages(this.elements.messagesContainer);
      this.showWelcome();
    }
  }

  /** Clean up and remove the panel */
  destroy(): void {
    this.isDestroyed = true;
    this.api.dispose();
    this.hideApiKeyPrompt();
    this.closeImmersiveMenu();
    this.destroyTopHandle();
    this.removeEventListeners();
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
      this.boundBeforeUnloadHandler = null;
    }
    this.clearAllTimers();
    this.state.dispose();
    // Clear detail store to prevent stale data
    resetDetailStore();
    this.elements?.panel.remove();
    this.elements = null;
    this.dropdownElement = null;
  }

  /** Clear all active timers to prevent callbacks after destroy */
  private clearAllTimers(): void {
    this.activeTimers.forEach(timerId => clearTimeout(timerId));
    this.activeTimers.clear();
  }

  // =============================================================================
  // EVENT LISTENERS
  // =============================================================================

  private setupEventListeners(): void {
    if (!this.elements) return;

    // Close button
    const closeBtn = this.elements.panel.querySelector('.pms-panel-close');
    if (closeBtn) {
      this.boundCloseHandler = () => this.callbacks.onClose();
      closeBtn.addEventListener('click', this.boundCloseHandler);
    }

    // Expand button (switch to sidebar)
    const expandBtn = this.elements.panel.querySelector('.pms-panel-expand');
    if (expandBtn) {
      expandBtn.addEventListener('click', () => this.callbacks.onStyleSwitch?.('vertical'));
    }

    // Immersive/vertical menu
    this.setupImmersiveMenuListeners();

    // Speed toggle
    if (this.elements.speedToggleBtn) {
      this.boundSpeedToggleHandler = () => this.toggleSpeedMode();
      this.elements.speedToggleBtn.addEventListener('click', this.boundSpeedToggleHandler);
    }

    // Send button
    if (this.elements.sendButton) {
      this.boundSendHandler = () => this.handleSend();
      this.elements.sendButton.addEventListener('click', this.boundSendHandler);
    }

    // Enter key to send (with IME composition guard for CJK input)
    if (this.elements.inputElement) {
      this.elements.inputElement.addEventListener('compositionstart', () => {
        this.isComposing = true;
      });
      this.elements.inputElement.addEventListener('compositionend', () => {
        this.isComposing = false;
      });

      this.boundInputKeypressHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && !this.isComposing && !e.isComposing) {
          e.preventDefault();
          this.handleSend();
        }
      };
      this.elements.inputElement.addEventListener('keydown', this.boundInputKeypressHandler);
    }

    // Delegated click handler for action buttons
    if (this.elements.messagesContainer) {
      this.boundMessagesClickHandler = (e: Event) => this.handleMessagesClick(e);
      this.elements.messagesContainer.addEventListener('click', this.boundMessagesClickHandler);
    }

    // Bubble drag-to-move
    this.setupBubbleDrag();
  }

  // ===========================================================================
  // BUBBLE DRAG
  // ===========================================================================

  private setupBubbleDrag(): void {
    const header = this.elements?.panel.querySelector('.pms-panel-header') as HTMLElement | null;
    if (!header) return;

    const DRAG_THRESHOLD = 5;

    const onStart = (clientX: number, clientY: number) => {
      // Only drag in bubble mode
      if (this.config.style !== 'bubble') return;
      const panel = this.elements?.panel;
      if (!panel) return;

      this.isDragging = true;
      this.dragDidMove = false;
      this.dragStartX = clientX;
      this.dragStartY = clientY;

      const rect = panel.getBoundingClientRect();
      this.dragPanelStartX = rect.left;
      this.dragPanelStartY = rect.top;

      panel.style.transition = 'none';
    };

    const onMove = (clientX: number, clientY: number) => {
      if (!this.isDragging) return;
      const panel = this.elements?.panel;
      if (!panel) return;

      const dx = clientX - this.dragStartX;
      const dy = clientY - this.dragStartY;

      if (!this.dragDidMove && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      this.dragDidMove = true;

      // Clamp within viewport
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pw = panel.offsetWidth;
      const ph = panel.offsetHeight;
      const newX = Math.max(0, Math.min(vw - pw, this.dragPanelStartX + dx));
      const newY = Math.max(0, Math.min(vh - ph, this.dragPanelStartY + dy));

      // Switch to top/left positioning
      panel.style.left = `${newX}px`;
      panel.style.top = `${newY}px`;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
    };

    const onEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      const panel = this.elements?.panel;
      if (panel) {
        panel.style.transition = '';
      }
    };

    // Mouse events
    this.boundDragMouseDown = (e: MouseEvent) => {
      // Ignore clicks on buttons inside header
      if ((e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      onStart(e.clientX, e.clientY);
    };
    this.boundDragMouseMove = (e: MouseEvent) => {
      if (!this.isDragging) return;
      e.preventDefault();
      onMove(e.clientX, e.clientY);
    };
    this.boundDragMouseUp = () => onEnd();

    // Touch events
    this.boundDragTouchStart = (e: TouchEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      const t = e.touches[0];
      if (t) onStart(t.clientX, t.clientY);
    };
    this.boundDragTouchMove = (e: TouchEvent) => {
      if (!this.isDragging) return;
      e.preventDefault();
      const t = e.touches[0];
      if (t) onMove(t.clientX, t.clientY);
    };
    this.boundDragTouchEnd = () => onEnd();

    header.addEventListener('mousedown', this.boundDragMouseDown);
    document.addEventListener('mousemove', this.boundDragMouseMove);
    document.addEventListener('mouseup', this.boundDragMouseUp);
    header.addEventListener('touchstart', this.boundDragTouchStart, { passive: false });
    document.addEventListener('touchmove', this.boundDragTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundDragTouchEnd);
  }

  /** Reset panel to default CSS position (used when switching styles) */
  resetDragPosition(): void {
    const panel = this.elements?.panel;
    if (panel) {
      panel.style.left = '';
      panel.style.top = '';
      panel.style.right = '';
      panel.style.bottom = '';
    }
  }

  private setupImmersiveMenuListeners(): void {
    const menuBtn = this.elements?.panel.querySelector('.pms-immersive-menu');
    if (menuBtn) {
      this.boundMenuHandler = (e: Event) => {
        e.stopPropagation();
        this.toggleImmersiveMenu();
      };
      menuBtn.addEventListener('click', this.boundMenuHandler);
    }

    // Close menu on outside click
    this.boundDocumentClickHandler = () => this.closeImmersiveMenu();
    document.addEventListener('click', this.boundDocumentClickHandler);

    // Close menu on ESC, or close panel if no menu is open
    this.boundDocumentKeydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.dropdownElement) {
          this.closeImmersiveMenu();
        } else if (this.config.style === 'bubble' && this.isOpen()) {
          this.callbacks.onClose();
        }
      }
    };
    document.addEventListener('keydown', this.boundDocumentKeydownHandler);
  }

  private handleMessagesClick(e: Event): void {
    const target = e.target as HTMLElement;

    // Action button
    const actionBtn = target.closest('.pms-action-btn');
    if (actionBtn instanceof HTMLElement) {
      const action = actionBtn.dataset['action'];
      if (action) this.handleAction(action);
      return;
    }

    // Copy button
    const copyBtn = target.closest('.pms-copy-btn');
    if (copyBtn instanceof HTMLElement) {
      this.handleCopy(copyBtn);
      return;
    }

    // Feedback buttons
    const feedbackBtn = target.closest('.pms-feedback-positive, .pms-feedback-negative');
    if (feedbackBtn instanceof HTMLElement) {
      const container = feedbackBtn.closest('.pms-message-actions');
      if (container instanceof HTMLElement) {
        const messageId = container.dataset['messageId'];
        const rating = feedbackBtn.dataset['rating'] as 'positive' | 'negative';
        if (messageId && rating) {
          this.handleFeedback(messageId, rating, feedbackBtn, container);
        }
      }
    }
  }

  private removeEventListeners(): void {
    // Document-level listeners
    if (this.boundDocumentClickHandler) {
      document.removeEventListener('click', this.boundDocumentClickHandler);
      this.boundDocumentClickHandler = null;
    }
    if (this.boundDocumentKeydownHandler) {
      document.removeEventListener('keydown', this.boundDocumentKeydownHandler);
      this.boundDocumentKeydownHandler = null;
    }
    // Panel element listeners
    if (this.elements) {
      const closeBtn = this.elements.panel.querySelector('.pms-panel-close');
      if (this.boundCloseHandler && closeBtn) {
        closeBtn.removeEventListener('click', this.boundCloseHandler);
      }
      if (this.boundSpeedToggleHandler && this.elements.speedToggleBtn) {
        this.elements.speedToggleBtn.removeEventListener('click', this.boundSpeedToggleHandler);
      }
      if (this.boundSendHandler && this.elements.sendButton) {
        this.elements.sendButton.removeEventListener('click', this.boundSendHandler);
      }
      if (this.boundInputKeypressHandler && this.elements.inputElement) {
        this.elements.inputElement.removeEventListener('keydown', this.boundInputKeypressHandler);
      }
      if (this.boundMessagesClickHandler && this.elements.messagesContainer) {
        this.elements.messagesContainer.removeEventListener('click', this.boundMessagesClickHandler);
      }
      const menuBtn = this.elements.panel.querySelector('.pms-immersive-menu');
      if (this.boundMenuHandler && menuBtn) {
        menuBtn.removeEventListener('click', this.boundMenuHandler);
      }
    }
    this.boundCloseHandler = null;
    this.boundSpeedToggleHandler = null;
    this.boundSendHandler = null;
    this.boundInputKeypressHandler = null;
    this.boundMessagesClickHandler = null;
    this.boundMenuHandler = null;

    // Bubble drag cleanup
    const header = this.elements?.panel.querySelector('.pms-panel-header') as HTMLElement | null;
    if (this.boundDragMouseDown && header) header.removeEventListener('mousedown', this.boundDragMouseDown);
    if (this.boundDragMouseMove) document.removeEventListener('mousemove', this.boundDragMouseMove);
    if (this.boundDragMouseUp) document.removeEventListener('mouseup', this.boundDragMouseUp);
    if (this.boundDragTouchStart && header) header.removeEventListener('touchstart', this.boundDragTouchStart);
    if (this.boundDragTouchMove) document.removeEventListener('touchmove', this.boundDragTouchMove);
    if (this.boundDragTouchEnd) document.removeEventListener('touchend', this.boundDragTouchEnd);
    this.boundDragMouseDown = null;
    this.boundDragMouseMove = null;
    this.boundDragMouseUp = null;
    this.boundDragTouchStart = null;
    this.boundDragTouchMove = null;
    this.boundDragTouchEnd = null;
  }

  // =============================================================================
  // IMMERSIVE TOP HANDLE (drag resize)
  // =============================================================================

  /**
   * Create top handle for immersive mode with branding + drag bar + menu
   */
  private createTopHandle(): void {
    if (!this.elements) return;
    const panel = this.elements.panel;

    this.topHandle = document.createElement('div');
    this.topHandle.className = 'pms-container-top-handle';

    // Branding (left) — company name only, no logo in chat
    const branding = document.createElement('div');
    branding.className = 'pms-top-handle-branding';

    const companyName = this.config.branding?.companyName;
    if (companyName) {
      const name = document.createElement('span');
      name.className = 'pms-top-handle-name';
      name.textContent = companyName;
      branding.appendChild(name);
    }

    this.topHandle.appendChild(branding);

    // Drag bar (center)
    const dragBar = document.createElement('div');
    dragBar.className = 'pms-container-top-handle-bar';
    this.topHandle.appendChild(dragBar);

    // Buttons (right)
    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = 'pms-top-handle-buttons';

    // Menu button
    const menuBtn = document.createElement('button');
    menuBtn.className = 'pms-menu-btn';
    menuBtn.type = 'button';
    menuBtn.title = 'Menu';
    menuBtn.setAttribute('aria-label', 'Menu');
    menuBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
      <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
    </svg>`;
    menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleTopHandleMenu();
    });
    buttonsContainer.appendChild(menuBtn);

    // Layout toggle button (switch between immersive ↔ vertical)
    this.layoutToggleBtn = document.createElement('button');
    this.layoutToggleBtn.className = 'pms-menu-btn';
    this.layoutToggleBtn.type = 'button';
    this.layoutToggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.switchLayout();
    });
    buttonsContainer.appendChild(this.layoutToggleBtn);

    // Bubble mode button (switch to bubble)
    const bubbleBtn = document.createElement('button');
    bubbleBtn.className = 'pms-menu-btn';
    bubbleBtn.type = 'button';
    bubbleBtn.title = 'Switch to bubble';
    bubbleBtn.setAttribute('aria-label', 'Switch to bubble chat');
    bubbleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>`;
    bubbleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.callbacks.onStyleSwitch?.('bubble');
    });
    buttonsContainer.appendChild(bubbleBtn);

    // Minimize button
    const minimizeBtn = document.createElement('button');
    minimizeBtn.className = 'pms-minimize-btn';
    minimizeBtn.type = 'button';
    minimizeBtn.title = 'Minimize';
    minimizeBtn.setAttribute('aria-label', 'Minimize');
    minimizeBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
      <polyline points="4 14 10 14 10 20"/>
      <polyline points="20 10 14 10 14 4"/>
      <line x1="14" y1="10" x2="21" y2="3"/>
      <line x1="3" y1="21" x2="10" y2="14"/>
    </svg>`;
    minimizeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Route through widget.close() so the MAIN bubble reappears
      // instead of showing the internal floating bubble.
      this.callbacks.onClose();
    });
    buttonsContainer.appendChild(minimizeBtn);

    this.topHandle.appendChild(buttonsContainer);

    // Insert at the beginning of the panel, before ambient/header
    panel.insertBefore(this.topHandle, panel.firstChild);

    // Hide the standalone immersive menu button (replaced by top handle menu)
    const standaloneMenu = panel.querySelector('.pms-immersive-menu');
    if (standaloneMenu) (standaloneMenu as HTMLElement).style.display = 'none';

    // Create floating bubble for minimized state
    this.createFloatingBubble();

    // Initialize active layout and set up appropriate resize (skip for bubble — no resize needed)
    this.activeLayout = this.style === 'vertical' ? 'vertical' : 'immersive';
    this.updateLayoutToggleIcon();

    if (this.style !== 'bubble') {
      if (this.activeLayout === 'vertical') {
        this.topHandle.style.cursor = 'default';
        this.setupVerticalResize();
      } else {
        this.containerTop = this.calcInitialContainerTop();
        panel.style.setProperty('--container-top', `${this.containerTop}px`);
        this.setupTopHandleResize();
      }
    }

    // Create floating layout toggle inside the panel (visible even when nav bar covers top handle)
    this.createFloatingLayoutToggle();

    // Auto-switch vertical → immersive on mobile
    const mql = window.matchMedia('(max-width: 600px)');
    const handleMobileChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in e ? e.matches : (e as MediaQueryListEvent).matches;
      if (matches && this.activeLayout === 'vertical') {
        this.switchLayout();
      }
    };
    mql.addEventListener('change', handleMobileChange as (e: MediaQueryListEvent) => void);
    handleMobileChange(mql);

    // On mobile, start immersive/overlay minimized so the site is visible first
    if (mql.matches && (this.style === 'immersive' || this.style === 'vertical')) {
      this.minimize();
    }
  }

  /**
   * Calculate initial --container-top from theme.panelHeight.
   * panelHeight can be a percentage ("60%"), viewport unit ("40vh"), or pixels ("500px").
   * Default: 40% of viewport (containerTop = 60% from top).
   */
  private calcInitialContainerTop(): number {
    const raw = this.config.theme?.panelHeight;
    const vh = window.innerHeight;
    const isMobile = window.innerWidth < 600;

    let top: number;

    if (!raw) {
      // Default: full viewport height (top at 0)
      top = 0;
    } else {
      const num = parseFloat(raw);
      if (isNaN(num)) {
        top = 0;
      } else if (raw.endsWith('%') || raw.endsWith('vh')) {
        // Treat as percentage of viewport: panelHeight=40% → top at 60%
        const clamped = Math.max(0, Math.min(100, num));
        top = Math.round(vh * (1 - clamped / 100));
      } else if (raw.endsWith('px')) {
        // Absolute pixels from the bottom
        const clamped = Math.max(0, Math.min(vh, num));
        top = Math.round(vh - clamped);
      } else {
        // Bare number — treat as percentage
        const clamped = Math.max(0, Math.min(100, num));
        top = Math.round(vh * (1 - clamped / 100));
      }
    }

    // On mobile, cap panel to 60% of viewport so the site remains visible
    if (isMobile) {
      const minTop = Math.round(vh * 0.4); // At least 40% of viewport above panel
      top = Math.max(top, minTop);
    }

    return top;
  }

  /**
   * Create floating bubble shown when panel is minimized
   */
  private createFloatingBubble(): void {
    if (!this.elements) return;

    const position = this.config.position ?? 'bottom-right';
    this.floatingBubble = document.createElement('button');
    this.floatingBubble.className = `pms-floating-bubble pms-bubble pms-bubble-${position}`;
    this.floatingBubble.setAttribute('aria-label', 'Open Chat');
    this.floatingBubble.setAttribute('type', 'button');

    this.floatingBubble.innerHTML = `
      <span class="pms-bubble-icon pms-bubble-icon-chat">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </span>
    `;

    this.floatingBubble.addEventListener('click', () => {
      this.restore();
    });

    // Append to the panel — fixed positioning keeps it visible
    this.elements.panel.appendChild(this.floatingBubble);
  }

  /**
   * Minimize panel to floating bubble
   */
  private minimize(): void {
    if (!this.elements) return;
    this.elements.panel.classList.add('pms-immersive-minimized');
    this.floatingBubble?.classList.add('pms-visible');
    this.pusher?.reset();
  }

  /**
   * Restore from minimized state
   */
  private restore(): void {
    if (!this.elements) return;
    this.elements.panel.classList.remove('pms-immersive-minimized');
    this.floatingBubble?.classList.remove('pms-visible');
    this.reapplyPush();
  }

  /** Re-apply push based on current layout and panel dimensions. */
  private reapplyPush(): void {
    if (!this.pusher || !this.elements) return;
    const panel = this.elements.panel;
    if (this.activeLayout === 'vertical') {
      this.pusher.pushRight(panel.getBoundingClientRect().width);
    } else if (this.activeLayout === 'immersive') {
      this.pusher.pushBottom(window.innerHeight - panel.getBoundingClientRect().top);
    }
  }

  // =============================================================================
  // LAYOUT TOGGLE (immersive ↔ vertical)
  // =============================================================================

  /**
   * Switch between immersive (bottom panel) and vertical (right sidebar) layouts.
   */
  private switchLayout(): void {
    if (!this.elements || !this.topHandle) return;
    const panel = this.elements.panel;

    if (this.activeLayout === 'immersive') {
      // → vertical
      panel.classList.remove('pms-panel-immersive');
      panel.classList.add('pms-panel-vertical');
      this.activeLayout = 'vertical';
      this.topHandle.style.cursor = 'default';
      this.teardownTopHandleResize();
      panel.style.removeProperty('--container-top');
      this.setupVerticalResize();
    } else {
      // → immersive
      panel.classList.remove('pms-panel-vertical');
      panel.classList.add('pms-panel-immersive');
      this.activeLayout = 'immersive';
      this.topHandle.style.cursor = 'ns-resize';
      this.teardownVerticalResize();
      panel.style.removeProperty('--panel-width');
      this.containerTop = this.calcInitialContainerTop();
      panel.style.setProperty('--container-top', `${this.containerTop}px`);
      this.setupTopHandleResize();
    }
    this.updateLayoutToggleIcon();
    this.updateFloatingLayoutToggle();
    this.reapplyPush();
  }

  /**
   * Update the layout toggle button icon based on active layout.
   */
  private updateLayoutToggleIcon(): void {
    if (!this.layoutToggleBtn) return;
    if (this.activeLayout === 'immersive') {
      // Show "switch to vertical" icon (sidebar)
      this.layoutToggleBtn.title = 'Switch to sidebar';
      this.layoutToggleBtn.setAttribute('aria-label', 'Switch to sidebar layout');
      this.layoutToggleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="15" y1="3" x2="15" y2="21"/>
      </svg>`;
    } else {
      // Show "switch to immersive" icon (bottom panel)
      this.layoutToggleBtn.title = 'Switch to bottom panel';
      this.layoutToggleBtn.setAttribute('aria-label', 'Switch to bottom panel layout');
      this.layoutToggleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="15" x2="21" y2="15"/>
      </svg>`;
    }
  }

  /**
   * Create a floating layout toggle button inside the panel.
   * This is visible even when the host site's nav bar covers the top handle.
   * Only shown in vertical mode; hidden in immersive mode (top handle is visible there).
   */
  private createFloatingLayoutToggle(): void {
    if (!this.elements) return;
    const panel = this.elements.panel;

    this.floatingLayoutToggle = document.createElement('button');
    this.floatingLayoutToggle.className = 'pms-floating-layout-toggle';
    this.floatingLayoutToggle.type = 'button';
    this.floatingLayoutToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      this.switchLayout();
    });
    this.updateFloatingLayoutToggle();

    panel.appendChild(this.floatingLayoutToggle);
  }

  /**
   * Update floating layout toggle visibility.
   * Hidden — the top handle already has the layout toggle button.
   */
  private updateFloatingLayoutToggle(): void {
    if (!this.floatingLayoutToggle) return;
    this.floatingLayoutToggle.style.display = 'none';
  }

  // =============================================================================
  // VERTICAL RESIZE (left-edge width drag)
  // =============================================================================

  /**
   * Set up left-edge drag handle for vertical panel width resize.
   */
  private setupVerticalResize(): void {
    if (!this.elements) return;
    const panel = this.elements.panel;

    // Create resize handle
    this.verticalResizeHandle = document.createElement('div');
    this.verticalResizeHandle.className = 'pms-vertical-resize-handle';
    panel.appendChild(this.verticalResizeHandle);

    const maxWidth = () => Math.round(window.innerWidth * 0.8);
    const minWidth = 280;

    this.boundLeftMouseMove = (e: MouseEvent) => {
      if (!this.isResizingLeft) return;
      e.preventDefault();
      const delta = this.resizeStartX - e.clientX;
      let newWidth = this.resizeStartWidth + delta;
      newWidth = Math.max(minWidth, Math.min(maxWidth(), newWidth));
      panel.style.setProperty('--panel-width', `${newWidth}px`);
      this.pusher?.updateDuringDrag(newWidth);
    };

    this.boundLeftMouseUp = () => {
      this.isResizingLeft = false;
      panel.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundLeftMouseMove) document.removeEventListener('mousemove', this.boundLeftMouseMove);
      if (this.boundLeftMouseUp) document.removeEventListener('mouseup', this.boundLeftMouseUp);
    };

    this.boundLeftTouchMove = (e: TouchEvent) => {
      if (!this.isResizingLeft || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0]!;
      const delta = this.resizeStartX - touch.clientX;
      let newWidth = this.resizeStartWidth + delta;
      newWidth = Math.max(minWidth, Math.min(maxWidth(), newWidth));
      panel.style.setProperty('--panel-width', `${newWidth}px`);
      this.pusher?.updateDuringDrag(newWidth);
    };

    this.boundLeftTouchEnd = () => {
      this.isResizingLeft = false;
      panel.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundLeftTouchMove) document.removeEventListener('touchmove', this.boundLeftTouchMove);
      if (this.boundLeftTouchEnd) {
        document.removeEventListener('touchend', this.boundLeftTouchEnd);
        document.removeEventListener('touchcancel', this.boundLeftTouchEnd);
      }
    };

    const startResize = (clientX: number) => {
      this.isResizingLeft = true;
      this.resizeStartX = clientX;
      this.resizeStartWidth = panel.getBoundingClientRect().width;
      panel.classList.add('pms-resizing');
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    };

    this.verticalResizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
      startResize(e.clientX);
      if (this.boundLeftMouseMove) document.addEventListener('mousemove', this.boundLeftMouseMove);
      if (this.boundLeftMouseUp) document.addEventListener('mouseup', this.boundLeftMouseUp);
    });

    this.verticalResizeHandle.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startResize(e.touches[0]!.clientX);
      if (this.boundLeftTouchMove) document.addEventListener('touchmove', this.boundLeftTouchMove, { passive: false });
      if (this.boundLeftTouchEnd) {
        document.addEventListener('touchend', this.boundLeftTouchEnd);
        document.addEventListener('touchcancel', this.boundLeftTouchEnd);
      }
    }, { passive: false });
  }

  /**
   * Tear down vertical resize handle and listeners.
   */
  private teardownVerticalResize(): void {
    if (this.verticalResizeHandle) {
      this.verticalResizeHandle.remove();
      this.verticalResizeHandle = null;
    }
    if (this.boundLeftMouseMove) document.removeEventListener('mousemove', this.boundLeftMouseMove);
    if (this.boundLeftMouseUp) document.removeEventListener('mouseup', this.boundLeftMouseUp);
    if (this.boundLeftTouchMove) document.removeEventListener('touchmove', this.boundLeftTouchMove);
    if (this.boundLeftTouchEnd) {
      document.removeEventListener('touchend', this.boundLeftTouchEnd);
      document.removeEventListener('touchcancel', this.boundLeftTouchEnd);
    }
    this.isResizingLeft = false;
  }

  /**
   * Tear down top handle (immersive) resize listeners.
   */
  private teardownTopHandleResize(): void {
    if (this.boundTopMouseMove) document.removeEventListener('mousemove', this.boundTopMouseMove);
    if (this.boundTopMouseUp) document.removeEventListener('mouseup', this.boundTopMouseUp);
    if (this.boundTopTouchMove) document.removeEventListener('touchmove', this.boundTopTouchMove);
    if (this.boundTopTouchEnd) {
      document.removeEventListener('touchend', this.boundTopTouchEnd);
      document.removeEventListener('touchcancel', this.boundTopTouchEnd);
    }
    // Remove mousedown/touchstart from topHandle
    if (this.topHandle && this.boundTopHandleMouseDown) {
      this.topHandle.removeEventListener('mousedown', this.boundTopHandleMouseDown);
    }
    if (this.topHandle && this.boundTopHandleTouchStart) {
      this.topHandle.removeEventListener('touchstart', this.boundTopHandleTouchStart);
    }
    this.isResizingTop = false;
  }

  /**
   * Toggle dropdown menu from top handle
   */
  private toggleTopHandleMenu(): void {
    const existing = this.topHandle?.querySelector('.pms-top-handle-dropdown');
    if (existing) {
      existing.remove();
      return;
    }

    const dropdown = document.createElement('div');
    dropdown.className = 'pms-top-handle-dropdown';
    dropdown.innerHTML = `
      <button class="pms-top-handle-dropdown-item" data-action="new-chat">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        New conversation
      </button>
      <button class="pms-top-handle-dropdown-item" data-action="clear">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        Clear chat
      </button>
      <div class="pms-immersive-dropdown-divider"></div>
      <button class="pms-top-handle-dropdown-item" data-action="switch-bubble">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Switch to bubble
      </button>
    `;

    dropdown.addEventListener('click', (e) => {
      e.stopPropagation();
      const item = (e.target as HTMLElement).closest('.pms-top-handle-dropdown-item') as HTMLElement;
      if (item) {
        const action = item.dataset['action'];
        if (action === 'new-chat') this.newSession();
        else if (action === 'clear') this.clear();
        else if (action === 'switch-bubble') this.callbacks.onStyleSwitch?.('bubble');
        dropdown.remove();
      }
    });

    // Close on outside click
    const closeDropdown = () => {
      dropdown.remove();
      document.removeEventListener('click', closeDropdown);
    };
    // Delay to avoid the current click closing it immediately
    requestAnimationFrame(() => {
      document.addEventListener('click', closeDropdown);
    });

    const buttonsContainer = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (buttonsContainer) {
      buttonsContainer.appendChild(dropdown);
    }
  }

  /**
   * Setup drag events for top handle
   */
  private setupTopHandleResize(): void {
    if (!this.topHandle || !this.elements) return;

    const panel = this.elements.panel;
    const maxTop = () => window.innerHeight - 10;

    this.boundTopMouseMove = (e: MouseEvent) => {
      if (!this.isResizingTop) return;
      e.preventDefault();
      const delta = e.clientY - this.resizeStartY;
      let newTop = this.resizeStartTop + delta;
      newTop = Math.max(0, Math.min(maxTop(), newTop));
      this.containerTop = newTop;
      panel.style.setProperty('--container-top', `${newTop}px`);
      this.pusher?.updateDuringDrag(window.innerHeight - newTop);
    };

    this.boundTopMouseUp = () => {
      this.isResizingTop = false;
      panel.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundTopMouseMove) document.removeEventListener('mousemove', this.boundTopMouseMove);
      if (this.boundTopMouseUp) document.removeEventListener('mouseup', this.boundTopMouseUp);
    };

    this.boundTopTouchMove = (e: TouchEvent) => {
      if (!this.isResizingTop || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0]!;
      const delta = touch.clientY - this.resizeStartY;
      let newTop = this.resizeStartTop + delta;
      newTop = Math.max(0, Math.min(maxTop(), newTop));
      this.containerTop = newTop;
      panel.style.setProperty('--container-top', `${newTop}px`);
      this.pusher?.updateDuringDrag(window.innerHeight - newTop);
    };

    this.boundTopTouchEnd = () => {
      this.isResizingTop = false;
      panel.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundTopTouchMove) document.removeEventListener('touchmove', this.boundTopTouchMove);
      if (this.boundTopTouchEnd) {
        document.removeEventListener('touchend', this.boundTopTouchEnd);
        document.removeEventListener('touchcancel', this.boundTopTouchEnd);
      }
    };

    const startResize = (clientY: number) => {
      this.isResizingTop = true;
      this.resizeStartY = clientY;
      this.resizeStartTop = this.containerTop;
      panel.classList.add('pms-resizing');
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    };

    this.boundTopHandleMouseDown = (e: MouseEvent) => {
      // Don't start drag from buttons
      if ((e.target as HTMLElement).closest('.pms-top-handle-buttons')) return;
      e.preventDefault();
      startResize(e.clientY);
      document.addEventListener('mousemove', this.boundTopMouseMove!);
      document.addEventListener('mouseup', this.boundTopMouseUp!);
    };
    this.topHandle.addEventListener('mousedown', this.boundTopHandleMouseDown);

    this.boundTopHandleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      if ((e.target as HTMLElement).closest('.pms-top-handle-buttons')) return;
      e.preventDefault();
      startResize(e.touches[0]!.clientY);
      document.addEventListener('touchmove', this.boundTopTouchMove!, { passive: false });
      document.addEventListener('touchend', this.boundTopTouchEnd!);
      document.addEventListener('touchcancel', this.boundTopTouchEnd!);
    };
    this.topHandle.addEventListener('touchstart', this.boundTopHandleTouchStart, { passive: false });
  }

  /**
   * Clean up top handle drag listeners
   */
  private destroyTopHandle(): void {
    if (this.boundTopMouseMove) {
      document.removeEventListener('mousemove', this.boundTopMouseMove);
      this.boundTopMouseMove = null;
    }
    if (this.boundTopMouseUp) {
      document.removeEventListener('mouseup', this.boundTopMouseUp);
      this.boundTopMouseUp = null;
    }
    if (this.boundTopTouchMove) {
      document.removeEventListener('touchmove', this.boundTopTouchMove);
      this.boundTopTouchMove = null;
    }
    if (this.boundTopTouchEnd) {
      document.removeEventListener('touchend', this.boundTopTouchEnd);
      document.removeEventListener('touchcancel', this.boundTopTouchEnd);
      this.boundTopTouchEnd = null;
    }
    this.topHandle?.remove();
    this.topHandle = null;
    this.floatingBubble?.remove();
    this.floatingBubble = null;
    this.floatingLayoutToggle?.remove();
    this.floatingLayoutToggle = null;
  }

  // =============================================================================
  // MESSAGE HANDLING
  // =============================================================================

  private showWelcome(): void {
    if (!this.elements?.messagesContainer) return;

    this.renderer.showWelcome(
      this.elements.messagesContainer,
      (suggestion) => this.handleSuggestionClick(suggestion),
      (action) => this.handleAction(action)
    );
  }

  private handleSuggestionClick(suggestion: string): void {
    if (this.elements?.inputElement) {
      this.elements.inputElement.value = suggestion;
      this.handleSend();
    }
  }

  private async handleSend(): Promise<void> {
    if (!this.elements?.inputElement) return;

    // If currently loading, treat as stop
    if (this.state.isCurrentlyLoading()) {
      this.stopGeneration();
      return;
    }

    const message = this.elements.inputElement.value.trim();
    if (!message) return;

    // Validate input
    const validation = validateInput(message);
    if (!validation.valid) {
      this.showError(validation.error || 'Invalid message');
      return;
    }
    if (validation.warning) {
      warn('Input validation warning:', validation.warning);
    }

    // Clear input
    this.elements.inputElement.value = '';

    // Add user message to UI
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };
    this.addMessage(userMessage);
    this.callbacks.onMessageSent?.(message);

    // Check for quick response
    const quickResponse = this.findQuickResponse(message);
    if (quickResponse) {
      debug('Using quick response for:', message);
      this.renderQuickResponse(quickResponse);
      return;
    }

    // Send to API
    await this.sendMessage(message);
  }

  private findQuickResponse(message: string): { text?: string; blocks: MessageBlock[] } | null {
    const quickResponses = this.config.quickResponses;
    if (!quickResponses || quickResponses.length === 0) {
      return null;
    }

    const lowerMessage = message.toLowerCase();

    for (const qr of quickResponses) {
      for (const pattern of qr.patterns) {
        if (lowerMessage.includes(pattern.toLowerCase())) {
          return qr.response;
        }
      }
    }

    return null;
  }

  private renderQuickResponse(response: { text?: string; blocks: MessageBlock[] }): void {
    const assistantMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'assistant',
      content: response.text || '',
      blocks: response.blocks,
      timestamp: new Date(),
    };
    this.addMessage(assistantMessage);
    this.callbacks.onResponseReceived?.(response.text || '', response.blocks);
  }

  private handleAction(action: string): void {
    if (!action) return;

    // Click-spam protection
    if (!this.state.canPerformAction(CLICK_DEBOUNCE_MS)) {
      debug('Ignoring rapid action:', action);
      return;
    }

    this.callbacks.onActionClick?.(action);

    // Demo switching
    if (action.startsWith('demo:')) {
      this.callbacks.onDemoSwitch?.(action.substring(5));
      return;
    }

    // List actions handled by cards component
    if (action.startsWith('list:')) {
      return;
    }

    // Cart actions handled by browse/cards — don't send as chat message
    if (action.startsWith('cart:')) {
      return;
    }

    // Detail view - sends message to get more details
    if (action.startsWith('detail:')) {
      const detailStore = getDetailStore();
      const item = detailStore.getActiveItem();
      const itemTitle = item?.title || action.substring(7);

      if (this.elements?.inputElement) {
        this.elements.inputElement.value = `Show me the full details for ${itemTitle}`;
        this.handleSend();
      }
      return;
    }

    // Navigate to detail page - fetches details via AI
    // Format: view-detail:ID|Title or view-detail:ID:type|Title
    if (action.startsWith('view-detail:')) {
      // Split off title (after |) first, then parse the rest
      const [actionPart = '', ...titleParts] = action.substring(12).split('|');
      const title = titleParts.join('|') || null;
      const parts = actionPart.split(':');
      const id = parts[0];
      const type = parts[1] || 'item';

      debug(`[Panel] View detail action: id=${id}, type=${type}, title=${title}`);

      // Send a natural message using the item title when available
      if (this.elements?.inputElement) {
        this.elements.inputElement.value = title
          ? `Tell me about ${title}`
          : `Show me the details for id ${id}`;
        this.handleSend();
      }

      // Also emit events for host application (optional navigation)
      this.callbacks.onActionClick?.(`navigate:${type}:${id}`);
      const event = new CustomEvent('pms-navigate', {
        bubbles: true,
        detail: { id, type, action }
      });
      document.dispatchEvent(event);
      return;
    }

    // Trigger a detail tool fetch - format: tool:get_player:abc123|Title
    // This sends a message that prompts Claude to call the specified tool with the id
    if (action.startsWith('tool:')) {
      // Split off title (after |) first, then parse the tool parts
      const [toolPart = '', ...titleParts] = action.substring(5).split('|');
      const title = titleParts.join('|') || null;
      const parts = toolPart.split(':');
      const toolName = parts[0];
      const id = parts[1];

      if (toolName && id) {
        debug(`[Panel] Tool action clicked: ${toolName} with id ${id}, title=${title}`);

        const displayMessage = title
          ? `Tell me about ${title}`
          : `Show me the details for id ${id}`;

        // Show friendly message in chat
        this.addMessage({
          id: generateMessageId(),
          role: 'user',
          content: displayMessage,
          timestamp: new Date(),
        });
        this.callbacks.onMessageSent?.(displayMessage);

        // Show loading feedback immediately
        this.showTypingIndicator();

        // Send enriched message to backend with tool/ID hint so
        // Haiku routes to the correct detail tool without guessing
        this.sendMessage(`${displayMessage} [detail:${toolName}:${id}]`);
      }
      return;
    }

    // Flow form submission — parse fields into a readable message
    if (action.startsWith('flow_submit:')) {
      this.handleFlowSubmit(action);
      return;
    }

    // Direct flow invocation - only if flow exists in available flows
    if (action.startsWith('flow:')) {
      const flowId = action.substring(5);
      const availableFlows = this.config.availableFlows ?? [];
      if (availableFlows.length === 0 || availableFlows.includes(flowId)) {
        this.handleDirectFlow(flowId);
      } else {
        // Flow not available - send as chat message instead
        debug(`[Panel] Flow "${flowId}" not in availableFlows, sending as message`);
        if (this.elements?.inputElement) {
          const flowName = flowId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          this.elements.inputElement.value = flowName;
          this.handleSend();
        }
      }
      return;
    }

    // Handle book: actions from calendar block - trigger booking flow form
    if (action.startsWith('book:')) {
      const slotParts = action.substring(5).split('|');
      const slotStart = slotParts[0] || '';
      const slotEnd = slotParts[1] || '';
      const flowId = slotParts[2] || '';
      const locationId = slotParts[3] || '';
      const meetingTypeId = slotParts[4] || '';
      const partySizeStr = slotParts[5] || '';

      // Format the time for display
      let timeDisplay = 'Selected time';
      try {
        const startDate = new Date(slotStart);
        timeDisplay = startDate.toLocaleString([], {
          weekday: 'long', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true
        });
      } catch { /* use default */ }

      // Parse party size (default 1)
      const parsedPartySize = partySizeStr ? parseInt(partySizeStr, 10) : 1;
      const effectivePartySize = isNaN(parsedPartySize) || parsedPartySize < 1 ? 1 : parsedPartySize;

      // Show user message
      const partySizeNote = effectivePartySize > 1 ? ` (party of ${effectivePartySize})` : '';
      this.addMessage({
        id: generateMessageId(),
        role: 'user',
        content: `I'd like to book: ${timeDisplay}${partySizeNote}`,
        timestamp: new Date()
      });

      // Store the booking slot data so it gets included in the flow submission
      this.state.bookingSlotData = { start: slotStart, end: slotEnd, locationId: locationId || undefined, meetingTypeId: meetingTypeId || undefined, partySize: effectivePartySize > 1 ? effectivePartySize : undefined };

      // If we have a booking flow ID, render the flow form directly (no AI round-trip)
      if (flowId) {
        this.addMessage({
          id: generateMessageId(),
          role: 'assistant',
          content: '',
          blocks: [{
            type: 'flow-form',
            data: { flowId, groupIndex: 0 }
          }],
          timestamp: new Date()
        });
        return;
      }

      // Fallback: send as a chat message so the AI can respond
      if (this.elements?.inputElement) {
        this.elements.inputElement.value = `I want to book the appointment slot at ${timeDisplay}. Please collect my contact details.`;
        this.handleSend();
      }
      return;
    }

    // Handle reschedule: actions from booking management block
    if (action.startsWith('reschedule:')) {
      const token = action.substring(11);
      this.addMessage({
        id: generateMessageId(),
        role: 'user',
        content: 'I would like to reschedule my booking',
        timestamp: new Date()
      });
      this.callbacks.onMessageSent?.('I would like to reschedule my booking');
      this.sendMessage(`reschedule booking [token:${token}]`);
      return;
    }

    // Handle cancel_booking: actions from booking management block
    if (action.startsWith('cancel_booking:')) {
      const token = action.substring(15);
      this.addMessage({
        id: generateMessageId(),
        role: 'user',
        content: 'I would like to cancel my booking',
        timestamp: new Date()
      });
      this.callbacks.onMessageSent?.('I would like to cancel my booking');
      this.sendMessage(`cancel booking [token:${token}]`);
      return;
    }

    // Handle link: actions
    if (action.startsWith('link:')) {
      const url = action.substring(5);
      navigateToUrl(url, this.shadowRoot);
      return;
    }

    // Handle mailto: and tel: URLs
    if (action.startsWith('mailto:') || action.startsWith('tel:')) {
      navigateToUrl(action, this.shadowRoot);
      return;
    }

    // Handle direct http/https URLs
    if (action.startsWith('http')) {
      navigateToUrl(action, this.shadowRoot);
      return;
    }

    // Send as message for other actions
    if (this.elements?.inputElement) {
      this.elements.inputElement.value = action;
      this.handleSend();
    }
  }

  private handleDirectFlow(flowId: string): void {
    const flowName = flowId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    this.addMessage({
      id: generateMessageId(),
      role: 'user',
      content: flowName,
      timestamp: new Date()
    });

    this.addMessage({
      id: generateMessageId(),
      role: 'assistant',
      content: '',
      blocks: [{
        type: 'flow-form',
        data: { flowId, groupIndex: 0 }
      }],
      timestamp: new Date()
    });
  }

  /**
   * Handle flow form submission.
   * Parses the encoded action string into readable key-value pairs,
   * shows a friendly user message, and sends to backend.
   * Format: flow_submit:flowId:groupIndex:key1=value1;key2=value2
   */
  private async handleFlowSubmit(action: string): Promise<void> {
    const parts = action.substring('flow_submit:'.length).split(':');
    const flowId = parts[0] || 'form';
    // groupIndex is parts[1], fieldData is everything after
    const fieldData = parts.slice(2).join(':');

    // Parse URL-encoded fields into readable pairs
    const fields: Array<{ key: string; value: string }> = [];
    if (fieldData) {
      for (const pair of fieldData.split(';')) {
        const eqIdx = pair.indexOf('=');
        if (eqIdx > 0) {
          const key = decodeURIComponent(pair.substring(0, eqIdx));
          const value = decodeURIComponent(pair.substring(eqIdx + 1));
          if (value) {
            fields.push({ key, value });
          }
        }
      }
    }

    // Build a human-readable flow name from the flowId
    const flowName = flowId.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    // Build readable display message for the chat
    const fieldSummary = fields
      .map(f => {
        const label = f.key.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        // For file fields, show just the filename instead of the full base64 blob
        if (f.value.startsWith('file:')) {
          const fileParts = f.value.split(':');
          if (fileParts.length >= 2) {
            return `${label}: ${decodeURIComponent(fileParts[1]!)}`;
          }
        }
        return `${label}: ${f.value}`;
      })
      .join(', ');
    const displayMessage = fieldSummary
      ? `${flowName} — ${fieldSummary}`
      : flowName;

    // Show clean user message in chat
    this.addMessage({
      id: generateMessageId(),
      role: 'user',
      content: displayMessage,
      timestamp: new Date(),
    });
    this.callbacks.onMessageSent?.(displayMessage);

    // 1. Capture submission in DB (fire-and-forget, non-blocking)
    const apiClient = this.api.getInternalApiClient();
    const sessionId = this.api.getSessionId();
    const submissionData: Record<string, string> = Object.fromEntries(fields.map(f => [f.key, f.value]));

    // Check for CV file upload — trigger matching if present
    const cvField = fields.find(f => f.value.startsWith('file:'));
    if (cvField && flowId === 'submit-cv' && this.config.vertical === 'recruitment') {
      const cvParts = cvField.value.split(':');
      if (cvParts.length >= 3) {
        const fileName = decodeURIComponent(cvParts[1]!);
        const fileContent = cvParts.slice(2).join(':'); // base64 may contain '=' padding

        // Strip the file content from submission data before storing
        // (don't persist the huge base64 in the flow submission)
        const cleanData = { ...submissionData };
        cleanData[cvField.key] = `[File: ${fileName}]`;

        // Submit the clean flow form (without file content)
        apiClient.submitFlowForm(this.config.workspaceId, {
          sessionId,
          flowId,
          flowName,
          data: cleanData,
        }).catch(() => {});

        // Call matching endpoint
        this.addMessage({
          id: generateMessageId(),
          role: 'assistant',
          content: 'Analyzing your CV and matching against available positions...',
          timestamp: new Date(),
        });

        try {
          const result = await apiClient.matchResume(this.config.workspaceId, fileName, fileContent);

          if (result && result.matches && result.matches.length > 0) {
            const jobList = result.matches.map(m =>
              `**${m.title}**${m.location ? ` — ${m.location}` : ''}${m.price ? ` | ${m.price}` : ''}\n${m.matchReason}`
            ).join('\n\n');

            this.addMessage({
              id: generateMessageId(),
              role: 'assistant',
              content: `Based on your CV, here are your top matches:\n\n${jobList}\n\nWould you like to apply for any of these positions?`,
              timestamp: new Date(),
            });
          } else {
            const msg = result?.noMatchMessage || 'No matching positions found at the moment. Check back later for new openings.';
            this.addMessage({
              id: generateMessageId(),
              role: 'assistant',
              content: msg,
              timestamp: new Date(),
            });
          }
        } catch (err) {
          logError('[Panel] Resume matching failed:', err);
          this.addMessage({
            id: generateMessageId(),
            role: 'assistant',
            content: 'Sorry, we were unable to match your CV at this time. Your application has been submitted and our team will review it manually.',
            timestamp: new Date(),
          });
        }
        return; // Skip the normal flow submission path
      }
    }

    // Inject booking slot data if this submission follows a calendar slot selection
    if (this.state.bookingSlotData) {
      submissionData['_booking_slot_start'] = this.state.bookingSlotData.start;
      submissionData['_booking_slot_end'] = this.state.bookingSlotData.end;
      if (this.state.bookingSlotData.locationId) {
        submissionData['_booking_location_id'] = this.state.bookingSlotData.locationId;
      }
      if (this.state.bookingSlotData.meetingTypeId) {
        submissionData['_booking_meeting_type_id'] = this.state.bookingSlotData.meetingTypeId;
      }
      if (this.state.bookingSlotData.partySize && this.state.bookingSlotData.partySize > 1) {
        submissionData['_booking_party_size'] = String(this.state.bookingSlotData.partySize);
      }
      this.state.bookingSlotData = null; // Consume once
    }

    apiClient.submitFlowForm(this.config.workspaceId, {
      sessionId,
      flowId,
      flowName,
      data: submissionData,
    }).catch(() => {}); // Never block on capture failure

    // 2. Show success confirmation locally — do NOT send back to Claude
    //    (sending it causes Claude to generate another form from the keywords)
    this.addMessage({
      id: generateMessageId(),
      role: 'assistant',
      content: `Thanks! Your ${flowName.toLowerCase()} has been submitted successfully. Is there anything else I can help with?`,
      timestamp: new Date(),
    });
  }

  private addMessage(message: ChatMessage): void {
    this.state.addMessage(message);

    if (this.elements?.messagesContainer) {
      this.renderer.renderMessage(this.elements.messagesContainer, message, {
        features: this.config.features,
        onAction: (action) => this.handleAction(action),
      });
    }

    // Persist conversation so it survives page navigations
    this.state.saveConversation(this.config.workspaceId);
  }

  private showError(message: string): void {
    if (!this.elements?.messagesContainer) return;
    this.renderer.showError(this.elements.messagesContainer, message);
  }

  private showUsageLimitError(): void {
    if (!this.elements?.messagesContainer) return;
    this.renderer.showUsageLimitError(this.elements.messagesContainer);
  }

  // =============================================================================
  // API COMMUNICATION
  // =============================================================================

  private async sendMessage(message: string): Promise<void> {
    const useStreaming = this.state.isStreamingEnabled();
    debug('sendMessage called, streaming:', useStreaming);

    if (this.state.isCurrentlyLoading()) return;

    this.state.setLoading(true);
    this.setSendButtonMode('stop');

    if (useStreaming) {
      await this.sendMessageStreaming(message);
    } else {
      await this.sendMessageNonStreaming(message);
    }
  }

  /**
   * Stop the current generation and return to ready state.
   */
  private stopGeneration(): void {
    debug('stopGeneration: aborting current stream');
    this.api.abort();
    this.hideTypingIndicator();
    this.finishLoading();
  }

  private async sendMessageStreaming(message: string): Promise<void> {
    // Detect language from user text — re-evaluate every message so a user
    // typing in a different language than the KB default gets the right response.
    const detected = detectLanguageFromText(message);
    if (detected) {
      const prev = this.state.getDetectedLanguage();
      if (prev !== detected) {
        debug('Language detected from text:', detected, prev ? `(was ${prev})` : '');
      }
      this.state.setDetectedLanguage(detected);
    } else if (!this.state.getDetectedLanguage()) {
      // First message with no text detection — use best-guess fallback:
      // config language → KB language (from backend) → HTML lang → widget UI default
      const configLang = this.config.language;
      const kbLang = this.config.kbLanguage;
      if (configLang && configLang.length >= 2) {
        this.state.setDetectedLanguage(configLang);
      } else if (kbLang && kbLang.length >= 2) {
        this.state.setDetectedLanguage(kbLang);
        debug('Using backend KB language hint:', kbLang);
      } else {
        const htmlLang = document.documentElement.lang?.split('-')[0]?.toLowerCase();
        if (htmlLang && htmlLang.length >= 2 && htmlLang !== 'en') {
          this.state.setDetectedLanguage(htmlLang);
          debug('Using page HTML lang attribute:', htmlLang);
        } else {
          this.state.setDetectedLanguage(getLanguage());
        }
      }
    }

    // Show typing indicator
    this.showTypingIndicator();

    // Guard: ensure elements exist before streaming
    const messagesContainer = this.elements?.messagesContainer;
    if (!messagesContainer) {
      this.finishLoading();
      return;
    }

    await this.api.sendStreaming(
      message,
      messagesContainer,
      (action) => this.handleAction(action),
      {
        onStart: () => {
          if (this.isDestroyed) return;
          this.hideTypingIndicator();
        },
        onLanguage: (lang) => {
          // Backend resolved language (via Lingua/Haiku) — switch widget
          // UI i18n bundle so browse sort labels, cart text, etc. match.
          const code = (lang.split('-')[0] ?? lang).toLowerCase();
          if (SUPPORTED_LANGUAGES.includes(code as SupportedLanguage)) {
            setLanguage(code as SupportedLanguage);
          }
        },
        onToolRequest: this.callbacks.onToolRequest
          ? async (request: ToolRequest): Promise<ToolResult> => {
              // Guard against tool requests after panel destruction
              if (this.isDestroyed) {
                return {
                  toolUseId: request.toolUseId,
                  toolName: request.toolName,
                  result: 'Panel was destroyed',
                  isError: true,
                  errorType: 'cancelled',
                };
              }
              return this.callbacks.onToolRequest!(request);
            }
          : undefined,
        onDone: (content, blocks) => {
          if (this.isDestroyed) return;
          this.state.addMessage({
            id: generateMessageId(),
            role: 'assistant',
            content,
            blocks,
            timestamp: new Date(),
          });
          // Persist conversation after streamed response completes
          this.state.saveConversation(this.config.workspaceId);
          this.callbacks.onResponseReceived?.(content, blocks);
          this.finishLoading();
        },
        onError: (error) => {
          if (this.isDestroyed) return;
          this.hideTypingIndicator();

          // Check for usage limit errors (workspace exceeded plan message limit)
          if (error.startsWith('USAGE_LIMIT:')) {
            this.showUsageLimitError();
            this.finishLoading();
            return;
          }

          // Check for rate limit errors (too many requests per hour)
          if (error.startsWith('RATE_LIMIT:')) {
            this.showError('Too many messages. Please wait a moment and try again.');
            this.finishLoading();
            return;
          }

          // Check for API key errors
          if (error.toLowerCase().includes('api key') || error.includes('401')) {
            this.state.setPendingMessage(message);
            this.showApiKeyPrompt();
            this.state.setLoading(false);
            this.setSendButtonMode('send');
            return;
          }

          this.showError(error);
          this.callbacks.onError?.(new Error(error));
          this.finishLoading();
        }
      },
      {
        speedMode: this.state.isSpeedModeEnabled(),
        language: this.state.getDetectedLanguage() || undefined,
        visitorAuthenticated: (this.config.isAuthenticated ?? isAuthenticatedFromStorage(this.config.authTokenKeys)) || undefined
      }
    );
  }

  private async sendMessageNonStreaming(message: string): Promise<void> {
    this.showTypingIndicator();

    const result = await this.api.sendNonStreaming(message);

    this.hideTypingIndicator();

    if (result.success && result.message) {
      this.addMessage(result.message);
      this.callbacks.onResponseReceived?.(result.message.content, result.message.blocks);
    } else if (result.requiresApiKey) {
      if (result.isInvalidKey) {
        this.api.clearApiKey();
        this.state.setPendingMessage(message);
        this.showApiKeyPrompt('Your API key is invalid. Please enter a valid key.');
      } else {
        this.state.setPendingMessage(message);
        this.showApiKeyPrompt();
      }
      this.state.setLoading(false);
      this.setSendButtonMode('send');
      return;
    } else {
      this.showError(result.error || 'Something went wrong. Please try again.');
      this.callbacks.onError?.(new Error(result.error));
    }

    this.finishLoading();
  }

  private finishLoading(): void {
    this.state.setLoading(false);
    this.setSendButtonMode('send');
    this.elements?.inputElement?.focus();
  }

  private setSendButtonMode(mode: 'send' | 'stop'): void {
    if (this.elements) {
      this.renderer.setSendButtonMode(this.elements, mode);
    }
  }


  // =============================================================================
  // TYPING INDICATOR
  // =============================================================================

  private showTypingIndicator(): void {
    if (!this.elements?.messagesContainer) {
      debug('showTypingIndicator: no messagesContainer');
      return;
    }
    if (this.typingIndicator) {
      debug('showTypingIndicator: already showing indicator');
      return;
    }
    debug('showTypingIndicator: creating indicator');
    this.typingIndicator = this.renderer.showTypingIndicator(this.elements.messagesContainer);
    debug('showTypingIndicator: indicator created', this.typingIndicator);
  }

  private hideTypingIndicator(): void {
    this.renderer.hideTypingIndicator(this.typingIndicator);
    this.typingIndicator = null;
  }

  // =============================================================================
  // API KEY MODAL
  // =============================================================================

  private showApiKeyPrompt(errorMessage?: string): void {
    const container = this.shadowRoot || this.elements?.panel.parentElement;
    if (!container) return;

    this.hideApiKeyPrompt();

    this.apiKeyModal = showApiKeyModal(container, {
      onSubmit: (apiKey: string) => {
        // Guard: prevent action if panel was destroyed while modal was open
        if (this.isDestroyed) return;

        this.api.setApiKey(apiKey);
        this.hideApiKeyPrompt();

        const pendingMsg = this.state.consumePendingMessage();
        if (pendingMsg) {
          this.sendMessage(pendingMsg);
        }
      },
      onCancel: () => {
        // Guard: prevent action if panel was destroyed while modal was open
        if (this.isDestroyed) return;

        this.state.setPendingMessage(null);
        this.hideApiKeyPrompt();
      }
    });

    if (errorMessage && this.apiKeyModal) {
      const errorDiv = this.apiKeyModal.querySelector('.pms-api-key-error') as HTMLElement;
      if (errorDiv) {
        errorDiv.textContent = errorMessage;
        errorDiv.style.display = 'block';
      }
    }
  }

  private hideApiKeyPrompt(): void {
    if (this.apiKeyModal) {
      hideApiKeyModal(this.apiKeyModal);
      this.apiKeyModal = null;
    }
  }

  // =============================================================================
  // IMMERSIVE MENU
  // =============================================================================

  private toggleImmersiveMenu(): void {
    if (this.dropdownElement) {
      this.closeImmersiveMenu();
      return;
    }

    this.dropdownElement = this.renderer.createImmersiveDropdown();

    this.dropdownElement.addEventListener('click', (e) => {
      e.stopPropagation();
      const item = (e.target as HTMLElement).closest('.pms-immersive-dropdown-item') as HTMLElement;
      if (item) {
        const action = item.dataset['action'];
        if (action === 'new-chat') this.newSession();
        else if (action === 'clear') this.clear();
        this.closeImmersiveMenu();
      }
    });

    this.elements?.panel.appendChild(this.dropdownElement);
    this.state.setState({ isMenuOpen: true });
  }

  private closeImmersiveMenu(): void {
    this.dropdownElement?.remove();
    this.dropdownElement = null;
    this.state.closeMenu();
  }

  // =============================================================================
  // SPEED MODE
  // =============================================================================

  private toggleSpeedMode(): void {
    const newSpeedMode = this.state.toggleSpeedMode();
    this.renderer.updateSpeedToggle(this.elements?.speedToggleBtn ?? null, newSpeedMode);
    debug('Speed mode toggled:', newSpeedMode);
  }

  // =============================================================================
  // COPY & FEEDBACK HANDLERS
  // =============================================================================

  private handleCopy(button: HTMLElement): void {
    const content = button.dataset['content'];
    if (!content) return;

    navigator.clipboard.writeText(content).then(() => {
      button.classList.add('pms-copied');
      button.title = 'Copied!';
      button.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      `;

      const timerId = setTimeout(() => {
        this.activeTimers.delete(timerId);
        button.classList.remove('pms-copied');
        button.title = 'Copy to clipboard';
        button.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        `;
      }, COPY_FEEDBACK_DURATION_MS);
      this.activeTimers.add(timerId);
    }).catch(err => {
      logError('Failed to copy:', err);
    });
  }

  private handleFeedback(
    messageId: string,
    rating: 'positive' | 'negative',
    button: HTMLElement,
    container: HTMLElement
  ): void {
    container.querySelectorAll('.pms-feedback-positive, .pms-feedback-negative').forEach(btn => {
      btn.classList.remove('pms-feedback-selected');
    });
    button.classList.add('pms-feedback-selected');

    this.api.submitFeedback(messageId, rating);
  }
}
